/** @jsx jsx */

import { Fragment as _, useState, useMemo, useEffect, useContext } from 'react';
import { jsx } from '@emotion/core';
import EditableText from 'components/lib/EditableText';
import renderBlock from 'services/apiHelpers/renderBlock';
import { Row, Column } from '@chameleoncreator/creator-ui';
import Image from 'components/lib/Image';
import ContinueBtn from 'components/lib/ContinueBtn/continueBtn_v2';
import Container from 'components/Layouts/ContainerLogical';
import Media, { MediaContext, IMediaContext } from 'components/lib/Media';
import Video from 'components/lib/Video';
import SVG from 'components/lib/SVG';
import { interactionContext as InteractionContext } from './interactionContext';
import { Block, Blocks } from '@chameleoncreator/creator-app';
import getBlockById from 'services/apiHelpers/getBlockById';
import ResultsPageContent from './ResultsPageContent';
import ResultsPageMedia from './ResultsPageMedia';
import { useSelector } from 'react-redux';
import ResultsPageResult from './ResultsPageResult';
import useIntersectionObserver from 'hooks/useIntersectionObserver';
import { getAllScoringInteractions } from 'services/scoring';
import getInteractionsInTopic from 'services/apiHelpers/getInteractionsInTopic';
import { State } from 'store/store';
import { findBlocksIn } from 'selectors/block';
import { createSelector } from 'reselect';
import pubSub from 'services/pubSub';
import { imgPlaceholders } from 'components/lib/Image';

const SingleColumnLayout = Row.bind({}) as any;
SingleColumnLayout.defaultProps = {
  ...(Row as any).defaultProps,
  flexDirection: 'column',
};

const TwoColumnLayout = Row.bind({}) as any;
TwoColumnLayout.defaultProps = {
  ...(Row as any).defaultProps,
  flexDirection: 'row',
};

const Components = {
  Row,
  Column,
  Text: EditableText,
  Image: ({ children, ...props }) => {
    return (
      <Image
        {...{
          ...props,
          placeholder:
            // only the 1 imgPlaceholder for now
            imgPlaceholders[1],
        }}
      />
    );
    //
  },
  TwoColumnLayout: TwoColumnLayout,
  SingleColumnLayout: SingleColumnLayout,
  ContinueButton: ContinueBtn,
  Video,
  SVG,
  Media,
  InteractionColumn: ({ children, ...props }) => {
    const interactionContext = useContext(InteractionContext);
    const { includeAdditionalTextField } = interactionContext.props;

    const [results, txt] = children;

    if (!includeAdditionalTextField) {
      return results;
    }

    return (
      <_>
        {results}
        <div
          style={{
            margin: '20px 0 0 0',
          }}
        >
          {txt}
        </div>
      </_>
    );
  },
  ResultsPageContent,
  ResultsPageContentPassed: ({ children }) => children[0],
  ResultsPageContentFailed: ({ children }) => children[0],
  ResultsPageResult,
  ResultsPageMedia,
};

/**
 * activeBlockSelector finds blocks that are accessible from the root block
 */
const activeBlockSelector = (blocks: Blocks, rootId: string): Block[] => {
  return findBlocksIn(blocks, blocks[rootId], (i: any) => i);
};

/**
 * scoringBlocks finds all the blocks that are scorable and have it turned on
 */
const scoringBlocks = (blocks: Block[]): string[] => {
  return blocks
    .filter((b: any) => {
      return b?.props?.score && b.props.score === true;
    })
    .map((b: any) => b.id);
};

let printResultsInteractionData;
const prettyPrintScoreData = (scoreData: {
  [key: string]: number | undefined;
}) => {
  return Object.keys(scoreData).reduce((acc, curr) => {
    const name = getBlockById(curr)?.props?.name;
    return {
      ...acc,
      [name]: scoreData[curr],
    };
  }, {});
};

/**
 * This selector pulls out all the active blocks from the store
 */
const loadActiveBlocks = createSelector(
  [(state: State) => state.blocks, (state: State) => state.rootBlockId!],
  activeBlockSelector
);

const loadScoringBlocks = createSelector(loadActiveBlocks, scoringBlocks);

const getModuleBlock = createSelector(
  (state: State) => state.blocks,
  (blocks: Blocks) => Object.values(blocks).find(b => b.type === 'Module')
);

const ResultsPageDisplay = props => {
  const interactionBlock = getBlockById(props.blockId);

  const [displayPassFailContent, setDisplayPassFailContent] = useState(
    'passed'
  );

  const { scoreData = {} } = useSelector((state: State) => ({
    scoreData: state.userBehaviour.score,
  }));

  const moduleBlock = useSelector(getModuleBlock);
  let mbProps = { passMark: 80 };
  if (moduleBlock) {
    mbProps = moduleBlock.props as { passMark: number };
  }

  const {
    scoringByTopic = false,
    scoringByTopicPassmark,
  } = interactionBlock?.props as any;

  const passMark =
    scoringByTopic && scoringByTopicPassmark
      ? scoringByTopicPassmark
      : mbProps.passMark;

  const scoringInteractions = useSelector(loadScoringBlocks);
  const numberOfScorableInteractions = scoringInteractions.length;

  // sketching out scorable interactions in topic
  const currentTopicBlock = useSelector((state: State) => {
    const { currentTopic, blocks } = state;

    return currentTopic ? blocks[currentTopic] : null;
  });

  const interactionsInTopic = getInteractionsInTopic(currentTopicBlock);

  const interactionsInTopicScoring = interactionsInTopic.filter(entry => {
    return entry.props?.score;
  });

  const interactionsInTopicIds = interactionsInTopic.map(entry => entry.id);

  // filter scoreData
  const topicScoreData = Object.keys(scoreData).reduce((acc, curr) => {
    return {
      ...acc,
      ...(interactionsInTopicIds.includes(curr) && {
        [curr]: scoreData[curr],
      }),
    };
  }, {});

  const allScoringInteractions = getAllScoringInteractions();

  const getScoreDataFromScoringInteractions = scoreData => {
    return allScoringInteractions.reduce((acc, curr) => {
      return {
        ...acc,
        [curr]: !!scoreData[curr] ? 1 : 0,
      };
    }, {});
  };

  const scoreDataFromScoringInteractions = getScoreDataFromScoringInteractions(
    scoringByTopic ? topicScoreData : scoreData
  );

  const scoreRaw = Object.values(scoreDataFromScoringInteractions).reduce<
    number
  >((acc, curr) => acc + Number(curr), 0);

  const scorePercent =
    ((scoreRaw || 0) /
      (scoringByTopic
        ? interactionsInTopicScoring.length
        : numberOfScorableInteractions)) *
      100 || 0;

  const hasScoringInteractions = !!allScoringInteractions?.length;

  const passed = hasScoringInteractions
    ? scorePercent >= Number(passMark)
    : true;

  const score = hasScoringInteractions ? scorePercent.toFixed() : 100;

  const { init } = useIntersectionObserver(true);

  useEffect(() => {
    init([document.querySelector(`#i${interactionBlock.id}`)], {
      threshold: 0.5,
    });
  }, []);

  useEffect(() => {
    // receives the event from the editor settings
    // show feedback for passed / failed
    const pS = pubSub.subscribe(
      `displayResultsInteractionPassFailContent_${interactionBlock.id}`,
      ({ displayPassFailContent }) => {
        setDisplayPassFailContent(displayPassFailContent);
      }
    );

    return pS;
  });

  clearTimeout(printResultsInteractionData);
  printResultsInteractionData = setTimeout(() => {
    console.log('RESULTS INTERACTION DATA :');
    console.log({
      hasScoringInteractions,
      numberOfScorableInteractions,
      scoringInteractions: scoringInteractions.map(
        id => getBlockById(id)?.props?.name
      ),
      topicScoreData: prettyPrintScoreData(topicScoreData),
      scoreData: prettyPrintScoreData(scoreData),
      scoreDataFromScoringInteractions,
      scoreRaw,
      scorePercent,
      passed,
    });
  }, 1000);

  const container = (
    <Container
      id={interactionBlock.id}
      key={interactionBlock.id}
      {...interactionBlock.props}
      {...props}
    >
      <InteractionContext.Provider
        value={{
          ...interactionBlock,
          displayPassFailContent,
          score,
          passed,
        }}
      >
        {(interactionBlock.children as Block[]).map(child => {
          return renderBlock(child, Components);
        })}
      </InteractionContext.Provider>
    </Container>
  );

  return window.dev()
    ? container
    : useMemo(() => {
        return container;
      }, [
        props.isOpen,
        displayPassFailContent,
        score,
        passed,
        interactionBlock.props!.lockDown,
      ]);
};

export default ResultsPageDisplay;
